import { cloneElement } from "react";
import {
  Box,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  BottomActionArea,
  BottomActionButton,
  FormBody,
  FormContentContainer,
  FormHeader,
} from "@components";

import { useLocation, useNavigate } from "react-router-dom";
import { DocumentsSvg, HomeSvg, PatientLinkSvg, PinSvg } from "./svg";

const MekidocFeaturesWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  padding: "24px 0px 48px 0px",
});

const MekidocFeaturesContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "48px",
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.25rem",
  fontWeight: 500,
  textAlign: "center",
  color: theme.palette.text.primaryDark,
}));

const CardContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "gap" && prop !== "maxWidth",
})<{ gap?: string; maxWidth?: string }>(({ gap, maxWidth, theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: gap ?? "24px",
  width: "100%",
  maxWidth: maxWidth ?? "100%",
  backgroundColor: theme.palette.background.default,
  padding: "16px",
  borderRadius: "16px",
}));

const Description = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  fontWeight: 400,
  textAlign: "center",
  color: theme.palette.text.black,
}));

const Bold = styled("span")({
  fontSize: "1rem",
  fontWeight: 600,
});

const StyledCheckCircleIcon = styled(CheckCircleIcon)(({ theme }) => ({
  fontSize: "80px",
  color: theme.palette.primary.main,
  marginBottom: "24px",
}));

interface FeaturesInfoProps {
  description: JSX.Element;
  component: JSX.Element;
}

const featuresInfo = [
  {
    description: (
      <Description>
        Comparte el <Bold>formulario para pacientes</Bold> y ellos completarán
        sus datos
      </Description>
    ),
    component: <PatientLinkSvg />,
  },
  {
    description: (
      <Description>
        Recibe y gestiona las solicitudes de tus pacientes en{" "}
        <Bold>un sólo lugar</Bold>
      </Description>
    ),
    component: <HomeSvg />,
  },
  {
    description: (
      <Description>
        Revisa y <Bold>crea fácilmente</Bold> documentos médicos
      </Description>
    ),
    component: <DocumentsSvg />,
  },
  {
    description: (
      <Description>
        Emite recetas con <Bold>Firma Electrónica Avanzada</Bold>
      </Description>
    ),
    component: <PinSvg />,
  },
] as const;

export const FeatureExplanationCard: React.FC<FeaturesInfoProps> = ({
  description,
  component,
}) => {
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const StyledFeatureComponent = cloneElement(component, {
    width: isSmUp ? "400px" : "300px",
    height: "auto",
  });
  return (
    <CardContainer>
      {description}
      {StyledFeatureComponent}
    </CardContainer>
  );
};

export function SuccessfulAuth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { redirectTo }: { redirectTo: string } = location.state || {
    redirectTo: "/app/solicitudes",
  };
  return (
    <>
      <FormHeader useMekiDocIcon />
      <FormContentContainer disableGutters>
        <FormBody>
          <StyledCheckCircleIcon />
          <Typography variant="h6" fontWeight={"semibold"}>
            Verificación exitosa.
          </Typography>
          <Description>Ya puedes emitir tu primer documento médico</Description>
          <MekidocFeaturesWrapper>
            <SectionTitle>¿Cómo funciona MekiDoc?</SectionTitle>
            <MekidocFeaturesContainer>
              {featuresInfo.map(({ description, component }, i) => (
                <FeatureExplanationCard
                  key={i}
                  description={description}
                  component={component}
                />
              ))}
            </MekidocFeaturesContainer>
          </MekidocFeaturesWrapper>
          <BottomActionArea>
            <BottomActionButton
              onClick={() => {
                navigate(redirectTo);
              }}
            >
              Comenzar
            </BottomActionButton>
          </BottomActionArea>
        </FormBody>
      </FormContentContainer>
    </>
  );
}
